import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  conversations: [],
  conversationsMeta: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  },
  conversationsLoading: false,
  activeConversationId: null,
  /** @type {Array<{role: string, content: string, richContent?: object, toolCalls?: object[], timestamp?: string}>} */
  messages: [],
  pendingConfirmation: null,
  isStreaming: false,
  streamingText: "",
  /** Steps for multi-step UI: { toolName, status: 'running'|'done'|'error' } */
  toolSteps: [],
  error: null,
  sidebarMobileOpen: false,
};

const agentSlice = createSlice({
  name: "agent",
  initialState,
  reducers: {
    setConversationsLoading(state, action) {
      state.conversationsLoading = action.payload;
    },
    setConversations(state, action) {
      state.conversations = action.payload.items || [];
      state.conversationsMeta = action.payload.meta || state.conversationsMeta;
    },
    setActiveConversation(state, action) {
      const { id, messages, pendingConfirmation } = action.payload;
      state.activeConversationId = id;
      state.messages = messages || [];
      state.pendingConfirmation = pendingConfirmation ?? null;
      state.streamingText = "";
      state.toolSteps = [];
    },
    clearActiveConversation(state) {
      state.activeConversationId = null;
      state.messages = [];
      state.pendingConfirmation = null;
      state.streamingText = "";
      state.toolSteps = [];
    },
    addUserMessage(state, action) {
      state.messages.push({
        role: "user",
        content: action.payload,
        timestamp: new Date().toISOString(),
      });
    },
    setStreaming(state, action) {
      state.isStreaming = action.payload;
      if (!action.payload) state.streamingText = "";
    },
    appendStreamToken(state, action) {
      state.streamingText += action.payload || "";
    },
    setStreamingText(state, action) {
      state.streamingText = action.payload;
    },
    finalizeAssistantMessage(state, action) {
      const { content, richContent, toolCalls } = action.payload;
      state.messages.push({
        role: "assistant",
        content: content || "",
        richContent: richContent ?? null,
        toolCalls: toolCalls || [],
        timestamp: new Date().toISOString(),
      });
      state.streamingText = "";
      state.toolSteps = [];
    },
    setPendingConfirmation(state, action) {
      state.pendingConfirmation = action.payload;
    },
    clearPendingConfirmation(state) {
      state.pendingConfirmation = null;
    },
    removeConversation(state, action) {
      state.conversations = state.conversations.filter(
        (c) => c.id !== action.payload
      );
      if (state.activeConversationId === action.payload) {
        state.activeConversationId = null;
        state.messages = [];
        state.pendingConfirmation = null;
      }
    },
    setAgentError(state, action) {
      state.error = action.payload;
    },
    clearAgentError(state) {
      state.error = null;
    },
    setSidebarMobileOpen(state, action) {
      state.sidebarMobileOpen = action.payload;
    },
    pushToolStep(state, action) {
      state.toolSteps.push(action.payload);
    },
    updateToolStep(state, action) {
      const { index, status } = action.payload;
      if (state.toolSteps[index]) {
        state.toolSteps[index].status = status;
      }
    },
    completeLastRunningTool(state) {
      for (let i = state.toolSteps.length - 1; i >= 0; i--) {
        if (state.toolSteps[i].status === "running") {
          state.toolSteps[i].status = "done";
          break;
        }
      }
    },
    resetToolSteps(state) {
      state.toolSteps = [];
    },
  },
});

export const {
  setConversationsLoading,
  setConversations,
  setActiveConversation,
  clearActiveConversation,
  addUserMessage,
  setStreaming,
  appendStreamToken,
  setStreamingText,
  finalizeAssistantMessage,
  setPendingConfirmation,
  clearPendingConfirmation,
  removeConversation,
  setAgentError,
  clearAgentError,
  setSidebarMobileOpen,
  pushToolStep,
  updateToolStep,
  completeLastRunningTool,
  resetToolSteps,
} = agentSlice.actions;

export default agentSlice.reducer;
